import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, IconButton } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { RxCross2 } from "react-icons/rx";
import { useGetPortfolioSummaryQuery } from "store/slices/apis/portfolioSummarySlice";
import { useGetAllProjectsQuery } from "store/slices/apis/projectApiSlice";
import Select from "react-select";
import { useUpdateOperatingCostMutation } from "store/slices/apis/operatingCostTrackerApiSlice";

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditOperatingCost = (props) => {
    const { open, handleClose, rowData, opexTypeOptions } = props;
    const { projectId: routeProjectId } = useParams();
    const dispatch = useDispatch();
    const [projectType, setProjectType] = useState(localStorage.getItem("projectType") || "all")

    const { data, error, isLoading, isFetching: isProjectFething, refetch } = useGetAllProjectsQuery(projectType);
    const { data: portfolioData, isFetching: portfolioLoader, isError: portfolioError } = useGetPortfolioSummaryQuery();

    const projectData = data?.data || [];
    const filteredProjectData = projectData.filter((data) => data.active == true);

    const projectOptions = (filteredProjectData || []).map(p => ({
        value: p.projectId,
        label: p.projectName,
        projectType: p.projectType,
    }));

    const portfolioOptions = (portfolioData?.data || []).map(p => ({
        value: p.portfolioName,
        label: p.portfolioName,
        projectType: p.portFolioType,
    }));

    const portfolioSelectOptions = useMemo(
        () => [
            // { projectType: "all", label: "All Portfolio" },
            ...portfolioOptions,
        ],
        [portfolioOptions]
    );

    const handlePortfolioChange = (option, setFieldValue) => {
        let projectType = option?.projectType || "all";
        setProjectType(projectType);
        localStorage.setItem("projectType", projectType)
        setFieldValue("product", option?.value || "");
    };

    const [updateOperatingCost] = useUpdateOperatingCostMutation();

    const [initialFormData, setInitialFormData] = useState({
        period: "",
        opexType: "",
        budget: null,
        actual: null,
        difference: null,
        product: "",
        project: "",
        projectId: "",
    });

    useEffect(() => {
        if (rowData && open) {
            setInitialFormData({
                period: rowData?.period ?? "",
                opexType: rowData?.opexType ?? "",
                budget: rowData?.budget ?? "",
                actual: rowData?.actual ?? "",
                difference: rowData?.difference ?? "",
                product: rowData?.product || "",
                project: rowData?.project || "",
                projectId: rowData?.projectId || ""
            });
        }
    }, [rowData, open, routeProjectId]);

    const validationSchema = Yup.object({
        period: Yup.string().trim().required("Period is required"),
        opexType: Yup.string().required("Opex Type is required"),
        product: Yup.string().required("Product is required"),
        project: Yup.string().required("Project is required"),
        budget: Yup.number()
            .typeError("Budget must be a number")
            .required("Budget is required")
            .min(0, "Budget cannot be negative"),
        actual: Yup.number()
            .typeError("Actual must be a number")
            .required("Actual is required")
            .min(0, "Actual cannot be negative"),
        difference: Yup.number()
            .typeError("Difference must be a number")
            .required("Difference is required"),
    });

    const stringToDecimal = (value) => {
        if (value === "" || value === null || value === undefined) return null;

        const num = Number(value);
        if (isNaN(num)) return null;

        return Number(num.toFixed(2));
    };

    const emptyGUID = "00000000-0000-0000-0000-000000000000";

    const handleSubmit = async (values, { setSubmitting }) => {
        const reqBody = {
            projectId: values?.projectId || "",
            operatingCostId: rowData?.operatingCostId,
            period: values?.period || "",
            opexType: values?.opexType || "",
            budget: stringToDecimal(values?.budget),
            actual: stringToDecimal(values?.actual),
            difference: stringToDecimal(values?.difference),
            product: values?.product || "",
            project: values?.project || ""
        };

        // console.log("Update payload:", reqBody);

        try {
            const response = await updateOperatingCost({ body: reqBody }).unwrap();

            openNotification(response.message || "Saved successfully", "success");
            setSubmitting(false);
            //if (refetchCounts) refetchCounts();
            handleClose();
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: { color: type },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    backgroundColor: "#FFFFFF",
                    width: "100%",
                    overflowX: "hidden"
                },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
                position: "relative",
            }}>
                <Typography sx={{
                    fontWeight: 600,
                    fontSize: "27px",
                    lineHeight: "100%",
                    color: "#140F36",
                    fontFamily: "Outfit, sans-serif"
                }}>
                    Edit Operating Cost
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose()}
                    sx={{
                        position: "absolute",
                        right: 50,
                        top: 8,
                        color: "#140F36",
                    }}
                >
                    <RxCross2 size={30} color="#140F36" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Formik
                                enableReinitialize={true}
                                initialValues={initialFormData}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ values, errors, submitCount, touched, handleChange, handleBlur, handleSubmit, setErrors, setFieldValue, setFieldTouched, isSubmitting }) => (
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <Typography sx={{ mt: 1, mb: 0, fontSize: "22px", fontWeight: 500, fontFamily: "Outfit, sans-serif", color: "#0A0A0C" }}>
                                                    Operating Cost Details
                                                </Typography>
                                            </Grid>
                                            {routeProjectId == undefined &&
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography sx={labelTextStyle}>
                                                        Product
                                                    </Typography>

                                                    <Select
                                                        name="product"
                                                        options={portfolioSelectOptions}
                                                        value={
                                                            portfolioSelectOptions.find(
                                                                (opt) => opt.value === values.product
                                                            ) || null
                                                        }
                                                        onChange={(option) => handlePortfolioChange(option, setFieldValue)}
                                                        onBlur={() => setFieldTouched("product", true)}
                                                        placeholder="Select Portfolio"
                                                    />

                                                    {(touched.product || submitCount > 0) && errors.product && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.product}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            }

                                            {routeProjectId == undefined &&
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography sx={labelTextStyle}>
                                                        Project
                                                    </Typography>

                                                    <Select
                                                        name="project"
                                                        options={[{ value: emptyGUID, label: "All Projects" }, ...projectOptions]}
                                                        value={
                                                            [{ value: emptyGUID, label: "All Projects" }, ...projectOptions]
                                                                .find(opt => opt.value === values.projectId) || null
                                                        }
                                                        onChange={(option) => {
                                                            setFieldValue("projectId", option?.value);
                                                            setFieldValue("project", option?.label);
                                                        }}
                                                        onBlur={() => setFieldTouched("project", true)}
                                                        placeholder="Select Project"
                                                    />

                                                    {(touched.project || submitCount > 0) && errors.project && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.project}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            }

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Period
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="period"
                                                    value={values.period}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Period"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.period && errors.period && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.period}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Opex Type</Typography>
                                                <Select
                                                    name="opexType"
                                                    options={opexTypeOptions}
                                                    value={opexTypeOptions.find(opt => opt.value === String(values.opexType)) || null}
                                                    onChange={(option) => setFieldValue("opexType", option.value)}
                                                    onBlur={() => setFieldTouched("opexType", true)}
                                                    placeholder="Select Opex Type"
                                                />
                                                {touched.opexType && errors.opexType && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.opexType}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Budget
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="budget"
                                                    value={values.budget}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Budget"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.budget && errors.budget && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.budget}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Actual
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="actual"
                                                    value={values.actual}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Actual"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.actual && errors.actual && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.actual}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Difference
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="difference"
                                                    value={values.difference}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Difference"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.difference && errors.difference && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.difference}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            <Grid size={12}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        alignItems: "center",
                                                        gap: 2,
                                                        mt: 2,
                                                    }}
                                                >
                                                    <Button onClick={handleClose}
                                                        sx={{
                                                            textTransform: "none",
                                                            color: "#F15744",
                                                            fontSize: "14px",
                                                            lineHeight: "100%",
                                                            fontFamily: "Outfit, sans-serif",
                                                            borderRadius: "25px",
                                                            fontWeight: 500,
                                                            px: "20px",
                                                            py: "15px",
                                                        }}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        disabled={isSubmitting}
                                                        sx={{
                                                            backgroundColor: "#00344C",
                                                            borderRadius: "25px",
                                                            textTransform: "none",
                                                            fontWeight: 500,
                                                            fontSize: "14px",
                                                            lineHeight: "100%",
                                                            fontFamily: "Outfit, sans-serif",
                                                            color: "#FFFFFF",
                                                            px: "20px",
                                                            py: "15px",
                                                            '&:disabled': {
                                                                backgroundColor: '#cccccc',
                                                            }
                                                        }}
                                                    >
                                                        {isSubmitting ? "Updating..." : "Update"}
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </form>
                                )}
                            </Formik>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default EditOperatingCost;